import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { Check, Search } from 'lucide-react';
import { useNavigate } from 'react-router';

import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { Client } from '@/features/console/console.types';
import {
  useCompanyOptions,
  useIsSuperAdmin,
} from '@/features/console/pages/clients/clients.hook';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/features/auth/auth.store';
import { useCompanyStore } from '@/stores/company.store';
import { useCompanySwitch } from '@/hooks/use-company-switch';
import { useMediaQuery } from '@/hooks/use-media-query';

/** How long to sit on a keystroke before asking the server, in milliseconds. */
const SEARCH_DEBOUNCE_MS = 400;

/**
 * Tailwind's `md`, the same breakpoint the sidebar itself switches on.
 *
 * Above it the sidebar is a narrow rail with the whole page to its right, so
 * the panel opens sideways. Below it the sidebar is a drawer taking ten
 * twelfths of the screen and there is no room to its right: Radix cannot flip
 * to the left either, because the panel does not fit on that side of the
 * trigger, so it stays put and runs off the edge. Opening downwards is the
 * only placement that fits.
 */
const DESKTOP_QUERY = '(min-width: 768px)';

/**
 * How many distinct hues an avatar can land on. Fine enough that a long
 * tenant list rarely repeats a colour, coarse enough that two neighbouring
 * hues still read as different colours.
 */
const AVATAR_HUE_STEPS = 24;

/**
 * Stable per-company colour so the avatar never changes between renders.
 *
 * Only the hue comes from the key: lightness and chroma stay fixed, so every
 * avatar keeps the same contrast against its white initials. FNV-1a rather
 * than a plain character sum, which spread UUIDs unevenly and gave anagrams
 * the same colour.
 */
function avatarColor(key = '') {
  let hash = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  const step = (hash >>> 0) % AVATAR_HUE_STEPS;
  return `oklch(58% 0.14 ${step * (360 / AVATAR_HUE_STEPS)})`;
}

function initials(name = '') {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function CompanyAvatar({
  company,
  className,
}: {
  company?: Client.IClient;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'flex size-10 flex-none items-center justify-center rounded-lg text-xs font-bold text-neutral-100',
        className
      )}
      style={{ backgroundColor: avatarColor(company?.id ?? company?.name) }}
    >
      {initials(company?.name)}
    </span>
  );
}

/**
 * Company (tenant) switcher that sits directly under the sidebar logo.
 * Expanded it is a card with the company avatar, name and address; collapsed
 * it reduces to the avatar alone, matching how the menu items reduce to icons.
 * Cmd/Ctrl+K opens it from anywhere.
 *
 * Super Admin only. Everyone else belongs to exactly one company and has
 * nothing to switch between, so the whole control is hidden rather than shown
 * with a single disabled option.
 */
function CompanySelector({ collapsed }: { collapsed: boolean }) {
  const isSuperAdmin = useIsSuperAdmin();
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const company = useCompanyStore(state => state.company);
  const setCompany = useCompanyStore(state => state.setCompany);
  const setUser = useAuthStore(state => state.setUser);
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  /** What is typed. `query` is what has actually been asked for. */
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  const pushQuery = useMemo(
    () => debounce((value: string) => setQuery(value), SEARCH_DEBOUNCE_MS),
    []
  );

  /**
   * What the list shows: whatever the server answered, whether that is the
   * first page while browsing or the matches for a search term. The browse-all
   * link below reaches everything this page leaves out.
   */
  const { data: rows, total } = useCompanyOptions(query);

  useEffect(() => () => pushQuery.cancel(), [pushQuery]);

  const onSearchChange = (value: string) => {
    setSearch(value);
    pushQuery(value);
  };

  const resetSearch = () => {
    pushQuery.cancel();
    setSearch('');
    setQuery('');
  };

  // Nothing picked yet. The store is persisted, so this is a first login
  // rather than a reload: prefer the company the account itself names, and
  // fall back to the first one otherwise. Silent either way — neither is a
  // switch the operator made.
  //
  // Taken straight off the account rather than looked up in `rows`: only
  // a page of five is fetched now that the endpoint does the searching, so the
  // operator's own company is very often not on it — and falling through to
  // `rows[0]` would silently open somebody else's tenant.
  useEffect(() => {
    if (company) return;

    const own = useAuthStore.getState().user?.company;
    if (own?.id) {
      setCompany(
        {
          id: own.id,
          name: own.name,
          address: own.address,
          status: own.status,
        },
        true
      );
      return;
    }

    if (rows.length === 0) return;
    setCompany(rows[0], true);
  }, [company, rows, setCompany]);

  /**
   * Mirror the selected tenant onto `user.company`.
   *
   * Roughly seventy places across the CMS already read the company they work in
   * from there — booking, dashboard, inventory, pricing, schedule, every async
   * select. Writing the switcher's choice into the same field makes all of them
   * follow it, instead of each one having to learn about this store.
   *
   * Only for a super Admin: for everyone else `user.company` is their real
   * company and must stay untouched. Guarded on the id so setting it cannot
   * feed itself.
   */
  useEffect(() => {
    if (!isSuperAdmin || !company?.id) return;

    const user = useAuthStore.getState().user;
    if (!user || user.company?.id === company.id) return;

    setUser({
      ...user,
      company: {
        ...user.company,
        id: company.id,
        name: company.name,
        address: company.address,
        status: company.status,
      },
    });
  }, [isSuperAdmin, company, setUser]);

  /**
   * Clear the previous tenant off the screen on a real switch.
   *
   * A tenant switch changes what every screen is about, not just the lists:
   * cached responses, page filters and the record a detail route names all
   * belong to the previous company, and one tenant's rows must never sit on
   * screen under another tenant's name. The hook drops the lot and leaves the
   * operator on the page they were reading, falling back to its list only when
   * the URL itself names a record of the company just left.
   */
  useCompanySwitch();

  if (!isSuperAdmin) return null;

  const name = company?.name ?? 'Select company';

  const trigger = (
    <button
      type="button"
      aria-label={`Company: ${name}`}
      className={cn(
        'bg-primary-10 hover:bg-primary-20 flex cursor-pointer items-center transition-colors',
        collapsed
          ? 'size-10 justify-center rounded-lg p-0'
          : 'w-full gap-3 rounded-xl p-3 text-left'
      )}
    >
      <CompanyAvatar company={company} />
      {!collapsed && (
        <span className="min-w-0 flex-1">
          <span className="text-text-primary block truncate font-bold">
            {name}
          </span>
          {company?.address && (
            <span className="text-base-orange-40 block truncate text-xs">
              {company.address}
            </span>
          )}
        </span>
      )}
    </button>
  );

  return (
    <Popover
      open={open}
      onOpenChange={next => {
        setOpen(next);
        if (!next) resetSearch();
      }}
    >
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="right">{name}</TooltipContent>
        </Tooltip>
      ) : (
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      )}

      <PopoverContent
        align="start"
        side={isDesktop ? 'right' : 'bottom'}
        // Keeps the panel off the very edge of a phone screen once it has
        // been shifted to fit, and caps it so a 288px panel cannot outgrow a
        // narrower viewport.
        collisionPadding={8}
        className="w-72 max-w-[calc(100vw-1rem)] p-1"
      >
        <div className="p-1 pb-2">
          {/* Radix focuses the first focusable child on open, so the search
              field is ready to type in without an autoFocus prop. */}
          <Input
            value={search}
            onChange={event => onSearchChange(event.target.value)}
            placeholder="Search tenants..."
            // `h-4 pr-2` is how every other search field in the CMS draws this
            // icon: the padding is what holds the divider off the glyph, and
            // leaving the width unset keeps `pr-2` from squashing it.
            prefixElement={<Search className="text-text-muted h-4 pr-2" />}
          />
        </div>

        {/* Only while searching. Browsing needs no header telling the
            operator that a tenant switcher lists tenants. */}
        {search.trim() && (
          <p className="text-text-muted px-2 pb-1 text-xs font-semibold tracking-wide uppercase">
            Results
          </p>
        )}

        <ul className="max-h-72 overflow-y-auto">
          {rows.length === 0 && (
            <li className="text-text-muted px-2 py-6 text-center text-sm">
              No tenant found
            </li>
          )}

          {rows.map(item => {
            const isActive = item.id === company?.id;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => {
                    setCompany(item);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2.5 rounded p-2 text-left transition-colors',
                    // Radix focuses the first row on open now that the search
                    // field is only drawn for long lists, so this replaces the
                    // browser's black default outline with the app's own ring.
                    'focus-visible:ring-1 focus-visible:ring-neutral-50 focus-visible:outline-none',
                    // `primary-20` sits a shade darker than the `neutral-80`
                    // hover, so the row in force always reads stronger than the
                    // one merely under the cursor.
                    isActive ? 'bg-primary-20' : 'hover:bg-neutral-80'
                  )}
                >
                  <CompanyAvatar company={item} className="size-8" />
                  <span className="min-w-0 flex-1">
                    <span className="text-text-primary block truncate text-sm font-semibold">
                      {item.name}
                    </span>
                    {item.address && (
                      <span className="text-text-muted block truncate text-xs">
                        {item.address}
                      </span>
                    )}
                  </span>
                  {isActive && (
                    <Check className="text-base-orange-40 size-4 flex-none" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        <div className="border-neutral-80 mt-1 border-t pt-1">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/console/clients');
            }}
            className="text-text-link hover:bg-neutral-80 flex w-full cursor-pointer items-center rounded px-2 py-2 text-sm focus-visible:ring-1 focus-visible:ring-neutral-50 focus-visible:outline-none"
          >
            See all {total} tenants
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { CompanySelector };
